import * as React from "react";
// tslint:disable-next-line:no-implicit-dependencies
import {Subscription} from "rxjs";
import {Buttons} from "../../../components/buttons/Buttons";
import {DateRange} from "../../../components/rangeddatepicker/RangedDatePickerController";
import {RangedDatePickerWithOptionsOptions} from "../../../components/rangeddatepicker/RangedDatePickerWithOptions";
import {AppTranslations} from "../../../components/Utils/translations/AppTranslations";
import "./AlarmsFilter.scss";
import {EquipmentAlarmsController} from "./EquipmentAlarmsController";
import {AlarmStateFilter, FilterDefaultOption} from "./subfilters/AlarmStateFilter";
import {copyObj, selectionOptionsFromTimezone} from "./subfilters/Commons";
import {PeriodFilter} from "./subfilters/PeriodFilter";
import {SeverityFilter, SeverityOption} from "./subfilters/SeverityFilter";
import {ThemeProvider} from "@vebox/etui-system";
import {veboxDefaultTheme} from "../../../index";
import {Display} from "../../../components/Display";
import {NumberInput} from "@vebox/etui-core";

declare var AppTranslations: AppTranslations;

export interface FilterConfig {
    received: { lookupPeriod: DateRange, lookupPeriodCode: number };
    alarmState: {
        pending: { active: boolean, lookupPeriod: DateRange, lookupPeriodCode: number };
        managed: { active: boolean, lookupPeriod: DateRange, lookupPeriodCode: number };
        closed: { active: boolean, lookupPeriod: DateRange, lookupPeriodCode: number };
    };
    alarmSeverity: SeverityOption;
    alarmCode: string;
}

export interface AlarmsFilterProps {
    displayCode: boolean;
    useCompletePeriods: boolean;
    controller: EquipmentAlarmsController;
    defaultFilterConfig: FilterConfig;
}

export interface AlarmsFilterState {
    disabled: boolean;
    isChanged: boolean;
    filter: FilterConfig;
    isDefault: boolean;
    numberOfAlarms?: number;
    closeAllOpenAlarmsButtonVisibility: boolean;
    selectionOptions: RangedDatePickerWithOptionsOptions[];
}

export class AlarmsFilter extends React.Component<AlarmsFilterProps, AlarmsFilterState> {

    private static showAllConfig: FilterConfig = {
        received: {
            lookupPeriod: {
                start: null,
                end: null,
            },
            lookupPeriodCode: 0,
        },
        alarmCode: "",
        alarmSeverity: {
            urgent: true,
            high: true,
            warning: true,
        },
        alarmState: {
            pending: {
                active: true,
                lookupPeriod: {
                    start: null,
                    end: null,
                },
                lookupPeriodCode: 0,
            },
            managed: {
                active: true,
                lookupPeriod: {
                    start: null,
                    end: null,
                },
                lookupPeriodCode: 0,
            },
            closed: {
                active: true,
                lookupPeriod: {
                    start: null,
                    end: null,
                },
                lookupPeriodCode: 0,
            },
        },
    };

    private stateFilterResetter: (o: FilterDefaultOption) => void;
    private periodFilterResetter: (o: number) => void;
    private severityFilterResetter: (o: SeverityOption) => void;

    private alarmCountListener: Subscription;
    private timezoneListener: Subscription;

    constructor(props) {
        super(props);

        this.state = {
            filter: this.props.useCompletePeriods
                ? copyObj(AlarmsFilter.showAllConfig)
                : props.defaultFilterConfig,
            disabled: false,
            isChanged: false,
            isDefault: true,
            closeAllOpenAlarmsButtonVisibility: false,
            selectionOptions: selectionOptionsFromTimezone(null, AppTranslations.equipmentAlarmFilter),
        };
    }

    public componentDidMount() {
        this.alarmCountListener = this.props.controller.listenToAlarmCountChange((count) => {
            this.setState({
                numberOfAlarms: count,
            });
        });

        this.timezoneListener = this.props.controller.listenForTimezone((timezone) => {
            this.setState(
                {selectionOptions: selectionOptionsFromTimezone(timezone, AppTranslations.equipmentAlarmFilter)});
        });

        if ((window as any).equipmentCloseAllOpenAlarmsButton) {
            (window as any).equipmentCloseAllOpenAlarmsButton.toggleButtonState = (state: boolean) => {
                this.setState({
                    closeAllOpenAlarmsButtonVisibility: state,
                });
            };
        }

    }

    public componentWillUnmount() {
        if (this.alarmCountListener) {
            this.alarmCountListener.unsubscribe();
        }
        if (this.timezoneListener) {
            this.timezoneListener.unsubscribe();
        }
        if ((window as any).equipmentCloseAllOpenAlarmsButton) {
            (window as any).equipmentCloseAllOpenAlarmsButton.toggleButtonState = undefined;
        }
    }

    public render() {
        const alarmFilterTranslations = AppTranslations.equipmentAlarmFilter;
        return (
            <ThemeProvider theme={veboxDefaultTheme}>
                <div id={"filter-wrapper"}>
                    <div className={"filter-form-row"}>
                        <PeriodFilter
                            disabled={this.state.disabled}
                            filter={this.state.filter}
                            onFilterConfigChanged={this.onFilterDataChanged}
                            resetter={this.setPeriodFilterResetter}
                            selectionOptions={this.state.selectionOptions}
                            defaultOption={this.props.useCompletePeriods
                                ? 0 : this.props.defaultFilterConfig.received.lookupPeriodCode}
                        />
                        <AlarmStateFilter
                            optionsDefaults={{
                                pending: this.props.defaultFilterConfig.alarmState.pending.lookupPeriodCode,
                                managed: this.props.defaultFilterConfig.alarmState.managed.lookupPeriodCode,
                                closed: this.props.defaultFilterConfig.alarmState.closed.lookupPeriodCode,
                                alwaysFirst: this.props.useCompletePeriods,
                            }}
                            disabled={this.state.disabled}
                            filter={this.state.filter}
                            onFilterConfigChanged={this.onFilterDataChanged}
                            resetter={this.setStateFilterResetter}
                            selectionOptions={this.state.selectionOptions}
                        />
                        <SeverityFilter
                            disabled={this.state.disabled}
                            filter={this.state.filter}
                            onFilterConfigChanged={this.onFilterDataChanged}
                            resetter={this.setSeverityFilterResetter}
                            defaultOption={this.props.defaultFilterConfig.alarmSeverity}
                        />
                        <Display isVisible={this.props.displayCode}>
                            <div className={"filter-form-column"}>
                                <p className={"filter-field-name"}>{alarmFilterTranslations.alarmCodeLabel}</p>
                                <NumberInput
                                    name={"Alarm code"}
                                    label={""}
                                    value={this.state.filter.alarmCode}
                                    disabled={this.state.disabled}
                                    onChange={
                                        // tslint:disable-next-line:jsx-no-lambda
                                        (v) => this.onAlarmCodeChange(v)
                                    }
                                    styles={{
                                        input: {
                                            height: "30px",
                                            m: "5px",
                                            bg: "var(--second-level-tabbar-button-bg)",
                                            color: "var(--second-level-tabbar-text-color)",
                                            border: "1px solid var(--second-level-tabbar-button-border)"
                                        }
                                    }}
                                />
                                {/*<input
                                    type="number"
                                    value={this.state.filter.alarmCode}
                                    onChange={this.onAlarmCodeChange}
                                    disabled={this.state.disabled}
                                />*/}
                            </div>
                        </Display>
                    </div>
                    <div className={"filter-form-actions"}>
                        <Display isVisible={this.state.numberOfAlarms}>
                            <div className={"filter-form-column"}>
                                <h1 className={"title"}>
                                    {alarmFilterTranslations.labelNumberOfAlarms} {this.state.numberOfAlarms}
                                </h1>
                            </div>
                        </Display>
                        <div className={"buttons"}>
                            <Display isVisible={!this.state.isDefault}>
                                <Buttons.ButtonWithIcon
                                    key={2}
                                    label={alarmFilterTranslations.filterResetLabel}
                                    code={"reset"}
                                    icon={"./images/buttons/icon-cancelFilter.png"}
                                    styles={{
                                        root: {
                                            mx: 2
                                        }
                                    }}
                                    selected={false}
                                    onClick={this.onFiltersReset}
                                />
                            </Display>
                            <Display isVisible={this.state.isChanged}>
                                <Buttons.ButtonWithIcon
                                    key={1}
                                    code={"save"}
                                    icon={"./images/buttons/icon-apply.png"}
                                    selected={false}
                                    onClick={this.onFiltersApply}
                                    styles={{
                                        root: {
                                            mx: 2
                                        }
                                    }}
                                />
                            </Display>
                            <Display isVisible={this.state.closeAllOpenAlarmsButtonVisibility}>
                                <Buttons.ButtonWithIcon
                                    key={2}
                                    code={"closeAllOpenAlarms"}
                                    label={alarmFilterTranslations.closeAllOpenAlarms || "Close all open alarms"}
                                    icon={"./images/buttons/icon-cancelAlarm.png"}
                                    selected={false}
                                    onClick={this.onAlarmClose}
                                />
                            </Display>
                        </div>
                    </div>
                </div>
            </ThemeProvider>
        );
    }

    private setStateFilterResetter = (resetter) => {
        this.stateFilterResetter = resetter;
    }

    private setPeriodFilterResetter = (resetter) => {
        this.periodFilterResetter = resetter;
    }

    private setSeverityFilterResetter = (resetter) => {
        this.severityFilterResetter = resetter;
    }

    private onAlarmCodeChange = (value: number) => {
        this.setState({
            ...this.state,
            isChanged: true,
            isDefault: false,
            filter: {
                ...this.state.filter,
                alarmCode: value.toString(),
            },
        }, () => this.onFilterDataChanged({
            ...this.state.filter,
            alarmCode: value.toString(),
        }));
    }

    private onFilterDataChanged = (newFilter: FilterConfig) => {
        this.setState({isChanged: true, isDefault: false, filter: newFilter}, () => this.onFiltersApply(null));
    }

    private onFiltersApply = (_) => {
        this.setState({isChanged: false}, () => {
            if (!this.state.isDefault) {
                this.props.controller.applyFilter(this.state.filter);
            }
        });
    }

    private onFiltersReset = (_) => {

        this.stateFilterResetter({
            pending: this.props.defaultFilterConfig.alarmState.pending.lookupPeriodCode,
            managed: this.props.defaultFilterConfig.alarmState.managed.lookupPeriodCode,
            closed: this.props.defaultFilterConfig.alarmState.closed.lookupPeriodCode,
        });

        this.periodFilterResetter(
            this.props.defaultFilterConfig.received.lookupPeriodCode,
        );

        this.severityFilterResetter(
            this.props.defaultFilterConfig.alarmSeverity,
        );

        this.setState({
            disabled: false,
            isChanged: false,
            isDefault: true,
            filter: copyObj(this.props.defaultFilterConfig),
        }, () => {
            this.props.controller.applyFilter(this.state.filter);
        });
    }

    private onAlarmClose = (_) => {
        if (!(window as any).equipmentCloseAllOpenAlarmsButton) {
            return;
        }
        const popupOpener = (window as any).equipmentCloseAllOpenAlarmsButton.openPopup;
        if (!popupOpener) {
            return;
        }
        popupOpener();
    }
}
